import dagre from '@dagrejs/dagre';
import { type FC, type ReactNode, useEffect, useMemo } from 'react';
import {
  type Edge,
  type Node,
  Position,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ArrowEdgeFlow, Box, theme } from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import {
  OFFSET_GRAPH_HEADING_HEIGHT,
  PAPER_FILE_SIZE,
  type PaymentNodeData,
  calculateAndFormatPaymentTotal,
  getMinistryName,
} from '@/features/mold/utils';
import type { IndirectCostModel } from '@/features/project/projects/_models/payment';
import type { PaymentEdgesModel } from '@/features/project/projects/_models/payment-edge';
import type { PaymentGroupsModel } from '@/features/project/projects/_models/payment-group';
import type { PaymentNodesMetaModel } from '@/features/project/projects/_models/payment-node-meta';
import { useLocaledAmount } from '@/providers';
import { BigCalculator, formatValueByUnit } from '@/utils';
import type { z } from 'zod';
import { PaymentGraphNode } from './PaymentGraphNode';

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 172;
const nodeHeight = 36;

const nodeTypes = {
  customNode: PaymentGraphNode,
};

const getLayoutedElements = (
  nodes: Array<Node<PaymentNodeData>>,
  edges: Edge[],
) => {
  dagreGraph.setGraph({ rankdir: 'TB' });

  for (const node of nodes) {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  for (const node of nodes) {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = Position.Top;
    node.sourcePosition = Position.Bottom;

    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  }

  return { nodes, edges };
};

const getEdges = ({
  paymentEdges,
  projectId,
}: {
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  projectId: string;
}) => {
  return paymentEdges.map<Edge>((paymentEdge) => ({
    id: paymentEdge.id,
    source: paymentEdge.isConnectedToSourceRoot
      ? projectId
      : (paymentEdge.sourceNodeId ?? ''),
    target: paymentEdge.targetNodeId,
    data: {
      label: paymentEdge.label,
      variant: 'secondary',
    },
  }));
};

type Props = {
  paymentGroups: z.infer<typeof PaymentGroupsModel>;
  paymentNodesMeta: z.infer<typeof PaymentNodesMetaModel>;
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  panelNode?: (nodes: Node<PaymentNodeData>[]) => ReactNode;
  projectId: string;
  ministryId: string;
  paymentIndirectCosts: z.infer<typeof IndirectCostModel> | null;
  hideBorder?: boolean;
};

export const PaymentGraphLayout: FC<Props> = ({
  paymentGroups,
  paymentNodesMeta,
  paymentEdges,
  panelNode,
  projectId,
  ministryId,
  paymentIndirectCosts,
  hideBorder,
}) => {
  const { unit } = useLocaledAmount();
  const { token } = theme.useToken();

  const ministries = useMinistries();
  const ministryName = getMinistryName({
    targetId: ministryId,
    ministries: ministries.data,
  });

  const defaultNodeOption = {
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    type: 'customNode',
    position: { x: 0, y: 0 },
  };
  const totalAmountOfIndirectCosts =
    paymentIndirectCosts?.costs.reduce(
      (previous, current) => BigCalculator.add(previous, current.amount ?? 0),
      0,
    ) ?? 0;
  const paymentIndirectCostTextList = useMemo(() => {
    if (
      !paymentIndirectCosts ||
      (!paymentIndirectCosts.label && paymentIndirectCosts.costs.length === 0)
    ) {
      return null;
    }
    return [
      [
        paymentIndirectCosts.label,
        paymentIndirectCosts.label ? ': ' : '',
        formatValueByUnit(totalAmountOfIndirectCosts, {
          operation: 'round',
          unit,
          showUnitLabel: true,
        }),
      ].join(''),
      ...paymentIndirectCosts.costs.map((cost, index) => {
        return [
          index + 1,
          '. ',
          cost.label,
          cost.label ? ': ' : '',
          formatValueByUnit(cost.amount ?? 0, {
            operation: 'round',
            unit,
            showUnitLabel: true,
          }),
        ].join('');
      }),
    ];
  }, [paymentIndirectCosts, totalAmountOfIndirectCosts, unit]);

  const rootNodes: Array<Node<PaymentNodeData>> = [
    {
      ...defaultNodeOption,
      id: projectId,
      data: {
        label: calculateAndFormatPaymentTotal({
          type: 'rootNodes',
          paymentEdges,
          paymentGroups,
          unit,
          totalAmountOfIndirectCosts,
        }),
        title: ministryName,
        paymentIndirectCostTextList,
      },
    },
  ];
  const initialNodes: Array<Node<PaymentNodeData>> = paymentGroups.map<
    Node<PaymentNodeData>
  >((paymentGroup) => ({
    ...defaultNodeOption,
    id: paymentGroup.id,
    data: {
      label: paymentGroup.totalAmount
        ? formatValueByUnit(paymentGroup.totalAmount, {
            operation: 'round',
            unit,
            showUnitLabel: true,
          })
        : calculateAndFormatPaymentTotal({
            type: 'initialNodes',
            paymentEdges,
            paymentGroups,
            paymentGroup,
            unit,
          }),
      displayCode: paymentGroup.displayCode,
      title: paymentGroup.name,
      paymentCount: paymentGroup.paymentCount,
      overview: paymentGroup.overview,
    },
  }));

  const initialEdges: Edge[] = getEdges({
    paymentEdges,
    projectId,
  });

  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    [...rootNodes, ...initialNodes],
    initialEdges,
  );
  const layoutedNodesByUser = layoutedNodes.map<Node<PaymentNodeData>>(
    (layoutedNode) => {
      const paymentNode = paymentNodesMeta.find(
        (paymentNode) => paymentNode.nodeId === layoutedNode.id,
      );
      if (!paymentNode?.position) {
        return layoutedNode;
      }
      return { ...layoutedNode, position: paymentNode.position };
    },
  );

  const [nodes, , onNodesChange] = useNodesState(layoutedNodesByUser);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  useEffect(() => {
    const updatedEdges: Edge[] = getEdges({
      paymentEdges,
      projectId,
    });

    setEdges(updatedEdges);
  }, [paymentEdges, projectId, setEdges]);

  return (
    <Box
      // To fit in A4 with RsProjectLedgerPage.
      height={`calc(${PAPER_FILE_SIZE.A4.width} - ${OFFSET_GRAPH_HEADING_HEIGHT})`}
      width={PAPER_FILE_SIZE.A4.height}
      border={hideBorder ? undefined : `1px solid ${token.colorBorder}`}
      borderRadius={hideBorder ? undefined : 8}
    >
      <ArrowEdgeFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        attributionPosition="bottom-left"
      >
        {panelNode?.(nodes)}
      </ArrowEdgeFlow>
    </Box>
  );
};
